import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Upload } from "lucide-react";
import api from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | error

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    let active = true;
    const poll = async () => {
      if (!active) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch (e) {}
      if (attempts >= 12) { setState("error"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center rounded-2xl border border-white/5 bg-[#141C42] p-10 fade-up" data-testid="payment-result">
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-[#E6B34A] animate-spin mb-6" />
            <h1 className="font-display font-black text-2xl tracking-tight">Confirming payment…</h1>
            <p className="text-zinc-400 mt-2">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-400/15 flex items-center justify-center mb-6">
              <CheckCircle2 className="w-9 h-9 text-emerald-400" />
            </div>
            <h1 className="font-display font-black text-3xl tracking-tighter" data-testid="payment-success">Payment successful!</h1>
            <p className="text-zinc-400 mt-3">Your commercial spot is reserved. Next, upload your audio ad and our team will review it before it goes on air.</p>
            <button onClick={() => navigate("/my-spots")} data-testid="go-upload" className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors">
              <Upload className="w-4 h-4" /> Upload your ad
            </button>
          </>
        )}
        {state === "error" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-[#FF3B30]/15 flex items-center justify-center mb-6">
              <XCircle className="w-9 h-9 text-[#FF3B30]" />
            </div>
            <h1 className="font-display font-black text-2xl tracking-tight">We couldn't confirm your payment</h1>
            <p className="text-zinc-400 mt-2">If you were charged, check My Spots shortly — it may take a minute to update.</p>
            <button onClick={() => navigate("/my-spots")} className="mt-8 px-6 py-3 rounded-xl bg-white/5 hover:bg-white/10 font-bold transition-colors">Go to My Spots</button>
          </>
        )}
      </div>
    </div>
  );
}
